import React from 'react';
import WrapperImage from '@components/image/WrapperImage.tsx';
import { formatMoney, formatPercentage, formatUnit } from '@utils/formats.ts';
import { CheapestTag, CheaperTag } from '@components/ui/PriceTag.tsx';
import { useProductModal } from '@hooks/useProductModal.ts';
import { PriceVariantDTO, UnitType } from '@api/generated/search-service';
import { isNotNullOrUndefined } from '@utils/utils.tsx';
import { useTranslation } from 'react-i18next';

interface ComponentProps {
  originalProduct: {
    name: string;
    supplierName: string;
    ecid: string;
    priceVariant: PriceVariantDTO | null;
    imageUrl?: string;
    auctionAmount: number;
    auctionUnit: UnitType;
  };
  alternateProducts: Array<{
    name: string;
    productId: number;
    supplierName: string;
    auctionPrice: number;
    imageUrl?: string;
    isCheapestAlternative: boolean;
    isCheaperAlternative: boolean;
  }>;
}

const AlternateProductsTab: React.FC<ComponentProps> = ({ alternateProducts, originalProduct }) => {
  const { openModal } = useProductModal();
  const { t } = useTranslation('client');

  return (
    <>
      <div className={'flex flex-col gap-6'}>
        <div className={'p-3 rounded-lg flex flex-col md:flex-row justify-between border-2 border bg-surface-light-neutral'}>
          {/*Image with names*/}
          <div className={'flex flex-row gap-2.5'}>
            <div className={'h-[112px] w-[112px]'}>
              <WrapperImage src={originalProduct.imageUrl ?? ''}></WrapperImage>
            </div>
            <div>
              <div className={'heading-desktop-heading-xs'}>{originalProduct.name}</div>
              <div className={'basic-body-s text-light'}>{originalProduct.supplierName}</div>
              <div className={'basic-body-s'}>{originalProduct.ecid}</div>
            </div>
          </div>
          {/*Prices*/}
          <div>
            <div className={'flex flex-row items-center mb-2 justify-between gap-[100px]'}>
              <div className={'basic-body-m-bold text-light'}>{t('alternatives.price')}</div>
              <div className={'basic-subtitle-xl-bold'}>{formatMoney(originalProduct.priceVariant?.currentPrice!!)}</div>
            </div>
            <div className={'flex flex-row basic-body-xs text-light justify-between'}>
              <div className={''}>{t('alternatives.auctionPrice')}</div>
              <div>
                {formatMoney(originalProduct.priceVariant?.auctionPrice!!)} / {originalProduct.auctionAmount} {formatUnit(originalProduct.auctionUnit as any)}
              </div>
            </div>
            <div className={'flex flex-row basic-body-xs text-light justify-between'}>
              <div className={''}>{t('alternatives.vatRate')}</div>
              <div>{formatPercentage(originalProduct.priceVariant?.vatPercentage!!)}</div>
            </div>
          </div>
        </div>
        <h3>{t('alternatives.title')}</h3>
        <div className={'grid grid-cols-1 gap-3 product-card-md:grid-cols-2 product-card-lg:grid-cols-3 overflow-scroll h-[700px] content-start'}>
          {alternateProducts.map((alternateProduct, index) => (
            <div
              key={index}
              className={'p-3 rounded-lg flex flex-col justify-between border-2 border hover:border-surface-dark-primary hover:border-2 cursor-pointer h-[189px]'}
              onClick={() => openModal(alternateProduct.productId, 'detail')}
            >
              {/*Image with names*/}
              <div className={'flex flex-row gap-2.5 mb-4'}>
                <div className={'h-[112px] w-[112px] shrink-0 relative overflow-hidden'}>
                  {alternateProduct.isCheapestAlternative === true && <CheapestTag />}
                  {alternateProduct.isCheapestAlternative === false && alternateProduct.isCheaperAlternative === true && <CheaperTag />}
                  <WrapperImage src={alternateProduct.imageUrl ?? ''}></WrapperImage>
                </div>
                <div>
                  <h5>{alternateProduct.name}</h5>
                  <div className={'basic-body-s text-light'}>{alternateProduct.supplierName}</div>
                </div>
              </div>
              {/*Prices*/}
              <div>
                <div className={'flex flex-row basic-body-xs text-light justify-between'}>
                  <div className={''}>{t('alternatives.auctionPrice')}</div>
                  <div>
                    {formatMoney(alternateProduct.auctionPrice)} / {originalProduct.auctionAmount} {formatUnit(originalProduct.auctionUnit as any)}
                  </div>
                </div>
                {isNotNullOrUndefined(originalProduct.priceVariant) && alternateProduct.isCheaperAlternative && (
                  <div className={'flex flex-row basic-subtitle-m-bold text-light justify-between'}>
                    <div className={''}>{t('alternatives.youSave')}</div>
                    <div className={'text-controls-surface-dark-secondary-default'}>
                      {formatPercentage(100 * (1 - alternateProduct.auctionPrice / originalProduct.priceVariant?.auctionPrice))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default AlternateProductsTab;
